// Echo guard for the Firestore snapshot-listener path.
//
// Problem: every mutation that goes through saveSlice / saveMisc / saveOpts /
// saveAppState writes to Firestore. Firestore then fires onSnapshot back at
// the same client, which — without guarding — would call setQueryData with
// data that's already in the cache, causing an unnecessary render pass and
// (on slow/contested renders) a visible flicker. Partners + owners on other
// tabs SHOULD see the update; only the writing tab should skip.
//
// Mechanism: each save stamps a monotonic millisecond timestamp against the
// slice name. The snapshot listener reads `_updatedAtMs` off the incoming
// doc. If the delta is within ECHO_WINDOW_MS of our own stamp, we assume
// this snapshot is the echo of our own write and skip the apply.
//
// Why a client-stamped `_updatedAtMs` (instead of serverTimestamp()):
// serverTimestamp() is null on the writer's local snapshot for the moment
// between write and server-ack (Firestore fires with an estimate); even
// once resolved, comparing against our local Date.now() introduces clock
// skew we can't quantify. A simple Date.now() stamp on write is reliable
// because the same clock is generating both values on the same tab. On
// OTHER tabs the stamp is a different clock's Date.now() — but those
// tabs don't hit the echo guard path (their lastLocalSaves map is empty
// for that slice).

type SliceName =
  | 'meta'
  | 'clients'
  | 'projects'
  | 'estimates'
  | 'expenses'
  | 'payments'
  | 'schedules'
  | 'misc'
  | 'attachments'

const lastLocalSaves = new Map<SliceName, number>()

/** Window within which an incoming snapshot is treated as a self-echo and
 *  skipped. 1500ms covers Firestore round-trip on a typical connection
 *  (~200–600ms) with headroom for congested networks, while staying short
 *  enough that legit external updates aren't swallowed by accident. */
export const ECHO_WINDOW_MS = 1500

/** Called by every save* helper immediately before the Firestore write. */
export function stampLocalSave(slice: SliceName): number {
  const ts = Date.now()
  lastLocalSaves.set(slice, ts)
  return ts
}

/** Called by the realtime listener when a snapshot arrives. Returns true
 *  if the snapshot should be IGNORED (because it's our own echo). */
export function isSelfEcho(slice: SliceName, docUpdatedAtMs: number | undefined): boolean {
  const stamped = lastLocalSaves.get(slice)
  if (!stamped) return false
  if (!docUpdatedAtMs) {
    // Doc has no client timestamp (e.g., a legacy save from before this
    // change, or a save path that bypasses stampLocalSave). Fall back to
    // "ignore if we just wrote within the window" — worse than exact
    // matching, but still prevents the immediate echo loop.
    return Date.now() - stamped < ECHO_WINDOW_MS
  }
  // Apply if the doc's timestamp is newer than or equal to our last save
  // stamp by anything OUTSIDE the window — that means a peer wrote after
  // we did. Skip when the doc's timestamp is within [stamped, stamped+window).
  return docUpdatedAtMs >= stamped && docUpdatedAtMs - stamped <= ECHO_WINDOW_MS
}

/** Test-only: clear all stamps. Never call from production code. */
export function __resetEchoGuardForTests(): void {
  lastLocalSaves.clear()
}
